import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

export const useEmployeeLeaves = ({ profile, language, t, onAlert }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [empModalStep, setEmpModalStep] = useState("select"); // "select" | "manage"
  const [empModalEmployees, setEmpModalEmployees] = useState([]);
  const [empModalLoading, setEmpModalLoading] = useState(false);
  const [empModalSearch, setEmpModalSearch] = useState("");
  const [selectedEmpForLeave, setSelectedEmpForLeave] = useState(null);
  const [empLeaves, setEmpLeaves] = useState([]);
  const [empLeavesLoading, setEmpLeavesLoading] = useState(false);
  const [deletingLeaveIds, setDeletingLeaveIds] = useState(new Set());
  const [newLeaveForm, setNewLeaveForm] = useState({ leaveType: "", customLeaveType: "", startDate: "", endDate: "", halfDayType: "", skipConfirmationNotification: false });
  const [createLeaveLoading, setCreateLeaveLoading] = useState(false);
  const [empQuota, setEmpQuota] = useState(null);
  const [empQuotaLoading, setEmpQuotaLoading] = useState(false);

  const resetModal = () => {
    setEmpModalStep("select");
    setSelectedEmpForLeave(null);
    setEmpLeaves([]);
    setEmpModalSearch("");
    setEmpQuota(null);
    setNewLeaveForm({ leaveType: "", customLeaveType: "", startDate: "", endDate: "", halfDayType: "" });
    fetchEmployees();
  };

  const fetchEmployees = async () => {
    setEmpModalLoading(true);
    try {
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const storedViewType = (localStorage.getItem("viewType") || "admin").toLowerCase();
      const managerCode = profile?.infinityCode || localStorage.getItem("infinityCode");
      const usp = new URLSearchParams();
      if (clientName) usp.set("clientName", clientName);
      if (storedViewType === "manager" && managerCode) usp.set("managerInfinityCode", managerCode);
      const url = `${BASE_URL}/employees/visibility?${usp.toString()}`;
      const resp = await apiFetch(url, { method: "GET", headers: { "Accept-Language": language, "x-view-type": storedViewType } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const list = json.employees || json.data || json;
      setEmpModalEmployees(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to fetch employees for modal:", err);
      setEmpModalEmployees([]);
    } finally {
      setEmpModalLoading(false);
    }
  };

  const fetchEmployeeLeaves = async (infinityCode) => {
    setEmpLeavesLoading(true);
    try {
      const url = `${BASE_URL}/api/leaves?infinityCode=${encodeURIComponent(infinityCode)}&limit=100`;
      const resp = await apiFetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      setEmpLeaves(result.data?.leaves || result.leaves || []);
    } catch (err) {
      console.error("Failed to fetch employee leaves:", err);
      setEmpLeaves([]);
    } finally {
      setEmpLeavesLoading(false);
    }
  };

  const fetchEmpQuota = async (infinityCode) => {
    setEmpQuotaLoading(true);
    try {
      const year = new Date().getFullYear();
      const resp = await apiFetch(
        `${BASE_URL}/api/leaves/${encodeURIComponent(infinityCode)}/quota?year=${year}`,
        { method: "GET" }
      );
      if (!resp.ok) throw new Error();
      const json = await resp.json();
      setEmpQuota(json?.data || json?.quota || json || null);
    } catch {
      setEmpQuota(null);
    } finally {
      setEmpQuotaLoading(false);
    }
  };

  const getLeaveTypeBalance = (leaveType) => {
    if (!empQuota) return null;
    const map = {
      annual: { quota: "annualQuota", used: "annualUsed" },
      sick: { quota: "sickQuota", used: "sickUsed" },
      maternity: { quota: "maternityQuota", used: "maternityUsed" },
      emergency: { quota: "emergencyQuota", used: "emergencyUsed" },
    };
    const keys = map[leaveType];
    if (!keys) return null;
    const total = Number(empQuota[keys.quota]) || 0;
    const used = Number(empQuota[keys.used]) || 0;
    return { total, used, remaining: Math.max(0, total - used) };
  };

  const handleLeaveFormChange = (field, value) => {
    setNewLeaveForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const selectEmployee = (emp) => {
    setSelectedEmpForLeave(emp);
    setEmpModalStep("manage");
    fetchEmployeeLeaves(emp.infinityCode);
    fetchEmpQuota(emp.infinityCode);
  };

  const goBackToSelect = () => {
    setEmpModalStep("select");
    setSelectedEmpForLeave(null);
    setEmpLeaves([]);
  };

  const handleDeleteEmpLeave = async (leaveId) => {
    const leave = empLeaves.find((l) => String(l.id) === String(leaveId));
    if (!leave) {
      onAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: language === "ar" ? "لم يتم العثور على الإجازة" : "Leave not found", variant: "error" });
      return;
    }
    const currentRole = (localStorage.getItem("viewType") || "").toLowerCase();
    const isAdmin = currentRole === "admin" || currentRole === "superadmin" || profile?.role === "admin" || profile?.role === "superadmin";
    if (!isAdmin) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(leave.startDate);
      startDate.setHours(0, 0, 0, 0);
      const cutoff = new Date(startDate);
      cutoff.setDate(cutoff.getDate() + 2);
      if (today > cutoff) {
        onAlert({ open: true, title: t.cannotDeletePastLeave, message: t.cannotDeletePastLeaveMsg, variant: "error" });
        return;
      }
    }
    setDeletingLeaveIds((prev) => new Set(prev).add(leaveId));
    try {
      const resp = await apiFetch(`${BASE_URL}/api/leaves/${leaveId}`, { method: "DELETE", headers: { "Content-Type": "application/json" } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setEmpLeaves((prev) => prev.filter((l) => l.id !== leaveId));
      if (selectedEmpForLeave?.infinityCode) fetchEmpQuota(selectedEmpForLeave.infinityCode);
      onAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.leaveDeletedSuccess, variant: "success" });
    } catch (err) {
      onAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
    } finally {
      setDeletingLeaveIds((prev) => { const s = new Set(prev); s.delete(leaveId); return s; });
    }
  };

  const handleCreateEmpLeave = async () => {
    const resolvedLeaveType = newLeaveForm.leaveType === "custom"
      ? newLeaveForm.customLeaveType.trim()
      : newLeaveForm.leaveType;

    // Debug log
    console.log("🔍 Form Data:", { newLeaveForm, resolvedLeaveType });

    // Validation checks with specific error messages
    if (!newLeaveForm.leaveType) {
      onAlert({ 
        open: true, 
        title: language === "ar" ? "تنبيه" : "Alert", 
        message: language === "ar" ? "يرجى اختيار نوع الإجازة" : "Please select a leave type", 
        variant: "error" 
      });
      return;
    }

    if (newLeaveForm.leaveType === "custom" && !resolvedLeaveType) {
      onAlert({ 
        open: true, 
        title: language === "ar" ? "تنبيه" : "Alert", 
        message: language === "ar" ? "يرجى كتابة نوع الإجازة المخصص" : "Please enter the custom leave type name", 
        variant: "error" 
      });
      return;
    }

    if (newLeaveForm.leaveType === "partialDay") {
      if (!newLeaveForm.startDate) {
        onAlert({ 
          open: true, 
          title: language === "ar" ? "تنبيه" : "Alert", 
          message: language === "ar" ? "يرجى اختيار التاريخ" : "Please select a date", 
          variant: "error" 
        });
        return;
      }
      if (!newLeaveForm.halfDayType) {
        onAlert({ 
          open: true, 
          title: language === "ar" ? "تنبيه" : "Alert", 
          message: language === "ar" ? "يرجى اختيار نوع الإجازة النصفية" : "Please select first or second half", 
          variant: "error" 
        });
        return;
      }
    } else {
      if (!newLeaveForm.startDate) {
        onAlert({ 
          open: true, 
          title: language === "ar" ? "تنبيه" : "Alert", 
          message: language === "ar" ? "يرجى اختيار تاريخ البداية" : "Please select start date", 
          variant: "error" 
        });
        return;
      }
      if (!newLeaveForm.endDate) {
        onAlert({ 
          open: true, 
          title: language === "ar" ? "تنبيه" : "Alert", 
          message: language === "ar" ? "يرجى اختيار تاريخ النهاية" : "Please select end date", 
          variant: "error" 
        });
        return;
      }
      if (new Date(newLeaveForm.startDate) > new Date(newLeaveForm.endDate)) {
        onAlert({ 
          open: true, 
          title: language === "ar" ? "تنبيه" : "Alert", 
          message: language === "ar" ? "تاريخ البداية يجب أن يكون قبل تاريخ النهاية" : "Start date must be before end date", 
          variant: "error" 
        });
        return;
      }
    }

    // Balance check
    const quotaTypes = ["annual", "sick", "maternity", "emergency"];
    if (quotaTypes.includes(resolvedLeaveType)) {
      const balance = getLeaveTypeBalance(resolvedLeaveType);
      if (balance !== null) {
        const days = newLeaveForm.leaveType === "partialDay"
          ? 0.5
          : Math.ceil(Math.abs(new Date(newLeaveForm.endDate) - new Date(newLeaveForm.startDate)) / (1000 * 60 * 60 * 24)) + 1;
        if (days > balance.remaining) {
          onAlert({ open: true, title: t.insufficientBalance, message: `${t.insufficientBalanceMsg}: ${balance.remaining} ${t.days}`, variant: "error" });
          return;
        }
      }
    }

    setCreateLeaveLoading(true);
    try {
      const payload = {
          infinityCode: selectedEmpForLeave.infinityCode,
          leaveType: newLeaveForm.leaveType === "partialDay" ? "PARTIAL_DAY_LEAVE" : resolvedLeaveType,
          reason: language === "ar" ? "إضافة يدوية من الإدارة" : "Manual addition by admin",
          myAdmin: profile?.infinityCode || localStorage.getItem("infinityCode"),
          halfDayType: newLeaveForm.halfDayType,
          skipConfirmationNotification: newLeaveForm.skipConfirmationNotification,
        };
      if (newLeaveForm.leaveType === "partialDay") {
        payload.date = newLeaveForm.startDate;
        payload.startDate = newLeaveForm.startDate;
        payload.endDate = newLeaveForm.startDate;
        payload.duration = 0.5;
      } else {
        payload.startDate = newLeaveForm.startDate;
        payload.endDate = newLeaveForm.endDate;
      }
      const resp = await apiFetch(`${BASE_URL}/api/leaves/admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || errBody.message || `HTTP ${resp.status}`);
      }
      const result = await resp.json();
      const newLeave = result.data || result.leave || result;
      if (newLeave?.id) setEmpLeaves((prev) => [newLeave, ...prev]);
      setNewLeaveForm({ leaveType: "", customLeaveType: "", startDate: "", endDate: "", halfDayType: "", skipConfirmationNotification: false });
      if (selectedEmpForLeave?.infinityCode) fetchEmpQuota(selectedEmpForLeave.infinityCode);
      onAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.leaveCreatedSuccess, variant: "success" });
    } catch (err) {
      onAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
    } finally {
      setCreateLeaveLoading(false);
    }
  };

  return {
    empModalStep,
    empModalEmployees,
    empModalLoading,
    empModalSearch,
    setEmpModalSearch,
    selectedEmpForLeave,
    empLeaves,
    empLeavesLoading,
    deletingLeaveIds,
    newLeaveForm,
    setNewLeaveForm,
    handleLeaveFormChange,
    createLeaveLoading,
    empQuota,
    empQuotaLoading,
    resetModal,
    selectEmployee,
    goBackToSelect,
    handleDeleteEmpLeave,
    handleCreateEmpLeave,
    getLeaveTypeBalance,
  };
};